import { useEffect, useRef, useState } from "react";
import { ArrowLeft, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Switch } from "@/components/ui/switch";
import { AppHeader } from "@/components/AppHeader";
import { api, API } from "@/lib/api";

const FIELDS = [
  ["company_name", "Company name"],
  ["address", "Address line"],
  ["phone", "Phone"],
  ["website", "Website"],
  ["sales_person", "Default sales person"],
];

export default function QuoteTemplateSettings() {
  const [t, setT] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoVer, setLogoVer] = useState(0);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings/quote-template").then(({ data }) => setT(data));
    api
      .get("/settings/branding")
      .then(({ data }) => setHasLogo(!!data.logo))
      .catch(() => setHasLogo(false));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/quote-template", t);
      setT(data);
      toast.success("Quote template saved");
    } catch {
      toast.error("Could not save the template");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/settings/logo", fd);
      setHasLogo(true);
      setLogoVer(Date.now());
      toast.success("Logo uploaded");
    } catch {
      toast.error("Logo upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  if (!t)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Settings / Quote template" />
        <main className="relative z-10 mx-auto max-w-3xl px-4 pt-10 text-zinc-500">
          Loading template…
        </main>
      </div>
    );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Settings / Quote template" />
      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-28 pt-6">
        <a
          href="/settings"
          data-testid="back-button"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> Settings
        </a>

        <div
          className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="quote-template-header-card"
        >
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Quotes
          </div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Quote Template
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Letterhead, notice wording and what the printed or emailed quote
            shows
          </p>
          <div className="mt-5 flex flex-wrap gap-2 border-t border-zinc-800 pt-4">
            <Button
              data-testid="save-template-button"
              disabled={busy}
              onClick={save}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              Save template
            </Button>
            <Button
              data-testid="upload-logo-button"
              variant="outline"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className="h-12 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              <UploadSimple size={15} weight="bold" className="mr-2" />
              {hasLogo ? "Replace logo" : "Upload logo"}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={upload}
              className="hidden"
              data-testid="logo-file-input"
            />
          </div>
        </div>

        <div
          className="mt-4 rounded-lg border border-zinc-800 bg-white p-5 text-slate-900"
          data-testid="template-letterhead-preview"
        >
          <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Letterhead preview · top of every quote PDF
          </div>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              {hasLogo ? (
                <img
                  src={`${API}/public/logo?v=${logoVer}`}
                  alt="Company logo"
                  data-testid="template-logo-preview"
                  className="mb-3 h-14 object-contain"
                />
              ) : (
                <button
                  type="button"
                  data-testid="template-logo-placeholder"
                  onClick={() => fileRef.current?.click()}
                  className="mb-3 flex h-14 w-40 items-center justify-center rounded-md border border-dashed border-slate-300 text-[11px] text-slate-400 hover:border-indigo-400 hover:text-indigo-500"
                >
                  + Add logo
                </button>
              )}
              <div className="text-lg font-bold tracking-tight">{t.company_name || "Your company"}</div>
              {t.address && <div className="text-[13px] text-slate-500">{t.address}</div>}
              {(t.phone || t.website) && (
                <div className="text-[13px] text-slate-500">
                  {[t.phone, t.website].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 text-right">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Quotation</div>
              <div className="font-mono-data text-xl font-bold tracking-tight">#QT-1001</div>
            </div>
          </div>
          {hasLogo && (
            <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
              <button
                type="button"
                data-testid="template-logo-replace"
                onClick={() => fileRef.current?.click()}
                className="text-[11px] font-bold uppercase tracking-widest text-indigo-600 hover:underline"
              >
                Replace logo
              </button>
              <button
                type="button"
                data-testid="template-logo-remove"
                onClick={async () => {
                  await api.delete("/settings/logo");
                  setHasLogo(false);
                  toast.success("Logo removed");
                }}
                className="text-[11px] font-bold uppercase tracking-widest text-red-500 hover:underline"
              >
                Remove
              </button>
            </div>
          )}
        </div>
        <p className="mt-2 text-[11px] text-zinc-500" data-testid="template-letterhead-note">
          Company name, address, phone and website are shared with Company settings — saving here updates both.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => (
            <div key={k} className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                {label}
              </Label>
              <Input
                data-testid={`template-${k}`}
                value={t[k] || ""}
                onChange={(e) => setT({ ...t, [k]: e.target.value })}
                className="h-11 border-zinc-800 bg-zinc-950"
              />
            </div>
          ))}
        </div>

        <div className="mt-4 space-y-3">
          {[
            ["notice_text", "Notice box wording"],
            ["terms_text", "Terms / fine print"],
            ["footer_text", "Footer line"],
          ].map(([k, label]) => (
            <div key={k} className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                {label}
              </Label>
              <Textarea
                data-testid={`template-${k}`}
                rows={k === "notice_text" ? 3 : 2}
                value={t[k] || ""}
                onChange={(e) => setT({ ...t, [k]: e.target.value })}
                className="border-zinc-800 bg-zinc-950"
              />
            </div>
          ))}
        </div>

        <div className="mt-4 space-y-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
          {[
            ["show_drawings", "Show the drawing of each area"],
            ["show_edge_images", "Show a cross-section picture of each edge profile"],
            ["show_piece_lines", "Show a line for every piece"],
          ].map(([k, label]) => (
            <div key={k} className="flex items-center justify-between gap-4">
              <span className="text-sm text-zinc-300">{label}</span>
              <Switch
                data-testid={`template-${k}`}
                checked={!!t[k]}
                onCheckedChange={(v) => setT({ ...t, [k]: v })}
              />
            </div>
          ))}
          <div className="border-t border-zinc-800 pt-3" data-testid="template-email-drawing">
            <span className="text-sm text-zinc-300">Drawing in the quote email</span>
            <p className="mb-2 text-xs text-zinc-500">
              The PDF and portal always follow the switch above; this only changes the email body.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {[
                ["dims", "With measurements"],
                ["plain", "Outline only"],
                ["none", "No drawing"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`template-email-drawing-${v}`}
                  onClick={() => setT({ ...t, email_drawing: v })}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                    (t.email_drawing || "dims") === v
                      ? "border-amber-400/60 bg-amber-500/15 text-amber-200"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-100"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4 space-y-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <span className="text-sm font-semibold text-zinc-100">
                Signable contract
              </span>
              <p className="text-xs text-zinc-500">
                Shown in the customer portal to read and sign after they accept
              </p>
            </div>
            <Switch
              data-testid="template-contract_enabled"
              checked={t.contract_enabled !== false}
              onCheckedChange={(v) => setT({ ...t, contract_enabled: v })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Contract title
            </Label>
            <Input
              data-testid="template-contract_title"
              value={t.contract_title || ""}
              onChange={(e) => setT({ ...t, contract_title: e.target.value })}
              className="h-11 border-zinc-800 bg-zinc-950"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Contract terms
            </Label>
            <RichTextEditor
              dataTestId="template-contract_body"
              value={t.contract_body || ""}
              onChange={(html) => setT({ ...t, contract_body: html })}
            />
            <p className="text-[11px] text-zinc-500">
              Format with the toolbar and use “Insert field…” to drop in details that fill
              automatically ({"{customer_name}"}, {"{project_name}"}, {"{material}"},{" "}
              {"{total}"}, {"{sqft}"}, {"{date}"}, and more).
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
